// IncomingCreateWebhookModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeIncomingAction } from '../../utils/integrationUtils';

// Constants
import { INCOMING_ACTION_OPTIONS } from '../../constants/integrationOptions';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * IncomingCreateWebhookModal
 *
 * Renders the modal used to create a new incoming webhook integration.
 */
const IncomingCreateWebhookModal = ({
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    incomingDraftName,
    incomingDraftAction,
    incomingDraftTypeId,
    typeRows,
    onDraftNameChange,
    onDraftActionChange,
    onDraftTypeIdChange,
    onCreate,
}) => {
    const isMobile = useIsMobile();
    const isCreating = busyActionKey === 'create-incoming-hook';

    return (
        <Modal
            iconKey="phishing"
            title="Add Webhook"
            isOpen={isOpen}
            width={isMobile ? '100%' : 500}
            maxHeight="85vh"
            onClose={onClose}
        >
            <ModalForm>
                <ModalField>
                    <ModalHint hint="Create an inbound endpoint other apps can use to send data into this knowledge base." />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Webhook name" />
                    <TextInput
                        value={incomingDraftName}
                        placeholder="Example: Google Sheets -> Tasks"
                        disabled={!canManageCredentials}
                        onChange={(event) => onDraftNameChange(event.target.value)}
                    />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Action" />
                    <SelectInput
                        value={incomingDraftAction}
                        disabled={!canManageCredentials}
                        options={INCOMING_ACTION_OPTIONS}
                        onChange={(event) => onDraftActionChange(normalizeIncomingAction(event.target.value))}
                    />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Target type" />
                    <SelectInput
                        value={incomingDraftTypeId}
                        disabled={!canManageCredentials || typeRows.length === 0}
                        options={typeRows.map((row) => ({
                            value: row.typeId,
                            label: row.label,
                        }))}
                        onChange={(event) => onDraftTypeIdChange(cleanString(event.target.value))}
                    />
                </ModalField>
            </ModalForm>

            <ModalButtons
                style={{
                    justifyContent: 'space-between',
                }}
            >
                <ModalButtonSection align="START">
                    <div />
                </ModalButtonSection>
                <ModalButtonSection align="END">
                    <Button
                        enabled={!isCreating}
                        iconKey="close"
                        label="Cancel"
                        onClick={onClose}
                    />
                    <Button
                        enabled={canManageCredentials && !isCreating}
                        iconKey="check"
                        label={isCreating ? 'Creating...' : 'Create Webhook'}
                        onClick={onCreate}
                    />
                </ModalButtonSection>
            </ModalButtons>
        </Modal>
    );
};

IncomingCreateWebhookModal.displayName = 'IncomingCreateWebhookModal';

export default IncomingCreateWebhookModal;
